import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchResolutionSettings } from "../api/backend.js";
import { applyResolutionToProducts, bonusLabel, enabledResolutions } from "../api/resolutions.js";
import { policyCopy } from "../utils/exchangePolicy.js";
import Page from "../components/layout/Page.jsx";
import SidePanel from "../components/SidePanel.jsx";
import { Alert, Badge, Button, Card, EmptyState, Spinner } from "../components/ui.jsx";
import { useOccurrence } from "../context/OccurrenceContext.jsx";
import { requestParentScroll, useIframeHeight } from "../hooks/useIframeHeight.js";
import { handlePrice } from "../utils/utils.js";
import styles from "./Resolution.module.css";

const Resolution = () => {
  const { shop, name, email } = useParams();
  const {
    occurrence,
    setOccurrence,
    resolutionSettings,
    setResolutionSettings,
    resolutionType,
    setResolutionType,
  } = useOccurrence();
  const [selected, setSelected] = useState(resolutionType || "");
  const [loading, setLoading] = useState(!resolutionSettings);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const options = useMemo(() => enabledResolutions(resolutionSettings), [resolutionSettings]);
  const selectedProducts = occurrence?.products || [];
  const subtotal = selectedProducts.reduce((sum, item) => sum + Number(item.price || 0), 0);

  useIframeHeight();

  useEffect(() => {
    if (!occurrence?.products?.length) {
      navigate(email ? `/${shop}/products/${name}/${email}` : `/${shop}`);
    }
  }, [email, name, navigate, occurrence, shop]);

  useEffect(() => {
    if (resolutionSettings) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    fetchResolutionSettings(shop)
      .then((settings) => {
        if (!cancelled) {
          setResolutionSettings(settings);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMessage("Não foi possível carregar as opções da loja.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [resolutionSettings, setResolutionSettings, shop]);

  const handleContinue = () => {
    if (!selected) {
      setMessage("Escolha uma forma de resolução.");
      return;
    }
    const products = applyResolutionToProducts(selectedProducts, selected);
    setResolutionType(selected);
    setOccurrence({
      ...occurrence,
      products,
      resolutionType: selected,
    });
    requestParentScroll();
    if (selected === "exchange") {
      navigate(`/${shop}/exchange/${name}/${encodeURIComponent(email)}`);
      return;
    }
    if (selected === "refund") {
      navigate(`/${shop}/refund/${name}${email ? `/${encodeURIComponent(email)}` : ""}`);
      return;
    }
    navigate(`/${shop}/post/${name}${email ? `/${encodeURIComponent(email)}` : ""}`);
  };

  if (loading) {
    return (
      <Page title="Como prefere resolver?" step="resolution" onBack={() => navigate(-1)}>
        <Card><Spinner label="Carregando opções da loja..." /></Card>
      </Page>
    );
  }

  // Opções com bônus primeiro; a de maior bônus vira a recomendada.
  const sortedOptions = [...options].sort((a, b) => (Number(b.bonusPercent) || 0) - (Number(a.bonusPercent) || 0));
  const recommendedType = Number(sortedOptions[0]?.bonusPercent) > 0 ? sortedOptions[0].type : null;
  const chosenOption = options.find((option) => option.type === selected);
  const chosenBonus = chosenOption ? (subtotal * (Number(chosenOption.bonusPercent) || 0)) / 100 : 0;
  const aside = (
    <SidePanel
      items={selectedProducts.map((item, index) => ({
        key: item.sku_id || index,
        image: item.image,
        title: item.title || item.name,
        meta: item.variantTitle,
        price: item.price,
      }))}
      totalLabel={chosenOption ? `${chosenOption.label}${chosenBonus ? " com bônus" : ""}` : "Valor dos itens"}
      total={subtotal + chosenBonus}
      note={chosenOption ? null : "Escolha uma forma de resolução para continuar."}
    >
      {message ? <Alert>{message}</Alert> : null}
      <Button onClick={handleContinue} disabled={options.length === 0}>
        Continuar
      </Button>
    </SidePanel>
  );

  return (
    <Page
      aside={aside}
      title="Como prefere resolver?"
      subtitle="O lojista libera as opções abaixo. O bônus, quando existir, entra no valor da resolução."
      step="resolution"
      onBack={() => navigate(-1)}
    >
      <div className={styles.stack}>
        {options.length === 0 ? (
          <Card>
            <EmptyState
              title="Nenhuma resolução disponível"
              description="A loja ainda não habilitou uma forma de resolver esta solicitação."
            />
          </Card>
        ) : (
          <div className={styles.options} role="radiogroup" aria-label="Forma de resolução">
            {sortedOptions.map((option) => {
              const bonus = bonusLabel(option);
              const recommended = option.type === recommendedType;
              const checked = selected === option.type;
              const projected = subtotal + (subtotal * (Number(option.bonusPercent) || 0)) / 100;
              return (
                <label key={option.type} className={`${styles.option} ${checked ? styles.optionChecked : ""}`}>
                  <input
                    type="radio"
                    name="resolutionType"
                    value={option.type}
                    checked={checked}
                    onChange={() => {
                      setSelected(option.type);
                      setMessage("");
                    }}
                  />
                  <span className={styles.optionBody}>
                    <span className={styles.optionTitle}>
                      {option.label}
                      {recommended ? <Badge tone="success">Recomendado</Badge> : null}
                      {bonus ? <Badge tone="success">{bonus}</Badge> : null}
                      <span className={styles.optionValue}>
                        {option.type === "exchange" ? "Sem custo" : `R$ ${handlePrice(projected)}`}
                      </span>
                    </span>
                    <span className={styles.optionCopy}>
                      {option.type === "exchange" ? policyCopy(option.policy) : option.description}
                    </span>
                    {option.bonusPercent > 0 ? (
                      <span className={styles.optionBonus}>
                        Valor com bônus: R$ {handlePrice(projected)}
                        {option.expiresInDays ? ` · válido por ${option.expiresInDays} dias` : ""}
                      </span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </Page>
  );
};

export default Resolution;
